import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { api } from '@/lib/api';
import { FarmMapView } from '@/components/dashboard/FarmMapView';
import { PondCreateModal, PondQuickViewModal } from '@/components/pond';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Card } from '@/components/ui/Card';
import { Search, Filter, AlertCircle, Plus } from 'lucide-react';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { useMembership } from '@/hooks/useMembership';
import type { FarmOverviewResponse, ApiResponse, PondOverview } from '@/types/api';

export const Route = createFileRoute('/_app/ponds/')({
  component: PondsIndexPage,
});

function PondsIndexPage() {
  const { farmId, isLoading: isFarmLoading } = useCurrentFarm();
  const { isSupervisorOrAbove } = useMembership();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedPond, setSelectedPond] = useState<PondOverview | null>(null);

  const { data, isLoading, error } = useQuery<ApiResponse<FarmOverviewResponse>>({
    queryKey: ['farm', 'overview', farmId],
    queryFn: () => api.get(`/api/v1/farms/${farmId}/overview`),
    staleTime: 60 * 1000,
    enabled: !!farmId,
  });

  const ponds = useMemo(() => data?.data?.ponds ?? [], [data]);

  const filteredPonds = useMemo(() => {
    return ponds.filter((pond: PondOverview) => {
      const matchesSearch = pond.name.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || pond.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [ponds, search, statusFilter]);

  if (isFarmLoading || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <LoadingSpinner size={32} className="text-primary-600" />
        <p className="text-gray-500">Loading ponds...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-status-critical/30">
        <Card.Body>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-status-critical" />
            </div>
            <h3 className="font-semibold text-gray-900">Failed to load ponds</h3>
            <p className="text-sm text-gray-500 mt-1">
              {error instanceof Error ? error.message : 'Please try again'}
            </p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ponds</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage and monitor all {ponds.length} {ponds.length === 1 ? 'pond' : 'ponds'}
          </p>
        </div>
        {isSupervisorOrAbove && (
          <Button
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Add Pond
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search ponds..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'idle', label: 'Idle' },
              { value: 'preparing', label: 'Preparing' },
              { value: 'harvesting', label: 'Harvesting' },
            ]}
          />
        </div>
      </div>

      {/* Results summary */}
      {(search || statusFilter !== 'all') && (
        <p className="text-sm text-gray-500">
          Showing {filteredPonds.length} of {ponds.length} {ponds.length === 1 ? 'pond' : 'ponds'}
          {search && <span> matching "{search}"</span>}
          {statusFilter !== 'all' && <span> with status "{statusFilter}"</span>}
        </p>
      )}

      {ponds.length === 0 && (
        <Card>
          <Card.Body>
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <p className="font-semibold text-gray-900">No ponds yet</p>
              <p className="max-w-sm text-sm text-gray-500">
                {isSupervisorOrAbove
                  ? 'Add the first pond to start entering readings and feedings.'
                  : 'A Farm Technician adds ponds. Ask yours to set this farm up.'}
              </p>
              {isSupervisorOrAbove && (
                <Button
                  variant="primary"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  Add Pond
                </Button>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Pond Cards */}
      <FarmMapView
        ponds={filteredPonds}
        alertThresholds={data?.data?.alert_thresholds}
        onPondClick={setSelectedPond}
      />

      {/* Quick-View Popup */}
      <PondQuickViewModal
        pond={selectedPond}
        open={selectedPond !== null}
        onClose={() => setSelectedPond(null)}
        alertThresholds={data?.data?.alert_thresholds}
      />

      {/* Create Pond Modal */}
      {farmId && (
        <PondCreateModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          farmId={farmId}
        />
      )}
    </div>
  );
}
